// src/components/EnterprisePassStatus.js
import React, { useState, useEffect } from 'react';
import { Building, Calendar, Users, AlertCircle } from 'lucide-react';
import { checkEnterpriseExpiration, getUserEnterpriseStatus } from '../services/enterprise';

const EnterprisePassStatus = ({ enterpriseStatus: initialEnterpriseStatus }) => {
  const [enterpriseStatus, setEnterpriseStatus] = useState(initialEnterpriseStatus);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Helper function to check if enterprise is expired by date
  const isEnterpriseExpiredByDate = (enterprise) => {
    if (!enterprise || !enterprise.enterpriseExpiresAt) return false;
    
    const expirationDate = new Date(enterprise.enterpriseExpiresAt);
    const currentDate = new Date();
    return currentDate > expirationDate;
  };
  
useEffect(() => {
  const checkAndUpdateEnterpriseStatus = async () => {
    if (!enterpriseStatus) return;
    
    const currentDate = new Date();
    const expirationDate = new Date(enterpriseStatus.enterpriseExpiresAt);
    const isExpiredByDate = currentDate > expirationDate;
    const currentStatus = enterpriseStatus.enterpriseStatus;
    
    // Case 1: Enterprise appears expired by date but status is still 'active'
    if (enterpriseStatus?.isEnterpriseMember && 
        !enterpriseStatus?.isRevokedEnterpriseMember &&
        currentStatus === 'active' &&
        isExpiredByDate) {
      
      console.log('Enterprise appears expired but status is still active, checking expiration...');
      setIsUpdatingStatus(true);
      
      try {
        const updateResult = await checkEnterpriseExpiration(enterpriseStatus.enterpriseId);
        console.log('Enterprise expiration check completed:', updateResult);
        
        // If status was updated, refresh the enterprise status
        if (updateResult.wasExpired) {
          console.log('Enterprise status was updated to expired, refreshing enterprise status...');
          const refreshedStatus = await getUserEnterpriseStatus();
          setEnterpriseStatus(refreshedStatus);
        }
      } catch (error) {
        console.error('Error checking enterprise expiration:', error);
        // Continue with current data even if background check fails
      } finally {
        setIsUpdatingStatus(false);
      }
    }
    
    // Case 2: Enterprise is NOT expired by date but status is 'expired' (stuck status)
    else if (enterpriseStatus?.isEnterpriseMember && 
             !enterpriseStatus?.isRevokedEnterpriseMember &&
             currentStatus === 'expired' &&
             !isExpiredByDate) {
      
      console.log('Enterprise is not expired by date but status is stuck at expired, triggering reactivation...');
      setIsUpdatingStatus(true);
      
      try {
        const updateResult = await checkEnterpriseExpiration(enterpriseStatus.enterpriseId);
        console.log('Enterprise reactivation check completed:', updateResult);
        
        // If status was updated from expired to active, refresh the enterprise status
        if (!updateResult.wasExpired) {
          console.log('Enterprise status was reactivated, refreshing enterprise status...');
          const refreshedStatus = await getUserEnterpriseStatus();
          setEnterpriseStatus(refreshedStatus);
        }
      } catch (error) {
        console.error('Error reactivating enterprise status:', error);
        // Continue with current data even if background check fails
      } finally {
        setIsUpdatingStatus(false);
      }
    }
    
    // Case 3: Status matches expiration state (log but no action needed)
    else if (enterpriseStatus?.isEnterpriseMember && 
             !enterpriseStatus?.isRevokedEnterpriseMember) {
      
      if (isExpiredByDate && currentStatus === 'expired') {
        console.log(`Enterprise is expired by date and status is already '${currentStatus}', no action needed`);
      } else if (!isExpiredByDate && currentStatus === 'active') {
        console.log(`Enterprise is not expired and status is already '${currentStatus}', no action needed`);
      }
    }
  };

  if (enterpriseStatus) {
    checkAndUpdateEnterpriseStatus();
  }
}, [enterpriseStatus]);
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Determine enterprise pass status
  const getEnterprisePassStatus = () => {
    if (!enterpriseStatus?.enterpriseStatus) return 'Unknown';
    
    if (enterpriseStatus.enterpriseStatus === 'active') {
      // Double-check if it's actually expired by date
      if (isEnterpriseExpiredByDate(enterpriseStatus)) {
        return 'Expired';
      }
      return 'Active';
    }
    
    return enterpriseStatus.enterpriseStatus.charAt(0).toUpperCase() + 
           enterpriseStatus.enterpriseStatus.slice(1);
  };

  const enterprisePassStatus = getEnterprisePassStatus();
  
  // Use the new explicit flag for revoked users
  const isRevokedUser = enterpriseStatus?.isRevokedEnterpriseMember;

  return (
    <>
      {/* Enterprise Pass Information */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Building className="mr-2" size={20} />
          Enterprise Access Pass
          {isUpdatingStatus && (
            <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
          )}
        </h2>
        
        {isRevokedUser ? (
          // REVOKED ENTERPRISE USER VIEW
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Membership</label>
              <div className="mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertCircle className="mr-1" size={12} />
                  Revoked
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Enterprise Pass</label>
              <div className="mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Not Available
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Organization</label>
              <p className="mt-1 text-sm text-gray-900">{enterpriseStatus.enterpriseName || 'Former Enterprise'}</p>
            </div>
            
            {enterpriseStatus.revokedAt && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Revoked On</label>
                <p className="mt-1 text-sm text-gray-900 flex items-center">
                  <Calendar className="mr-1" size={16} />
                  {formatDate(enterpriseStatus.revokedAt)}
                </p>
              </div>
            )}
            
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-4">
                Your enterprise access was revoked. Contact your administrator to restore access.
              </p>
            </div>
          </div>
        ) : (
          // ACTIVE ENTERPRISE USER VIEW
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Membership</label>
              <div className="mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <Users className="mr-1" size={12} />
                  Active
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Enterprise Pass</label>
              <div className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  enterprisePassStatus === 'Active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {enterprisePassStatus === 'Active' ? (
                    <Building className="mr-1" size={12} />
                  ) : (
                    <AlertCircle className="mr-1" size={12} />
                  )}
                  {enterprisePassStatus}
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Organization</label>
              <p className="mt-1 text-sm text-gray-900">{enterpriseStatus.enterpriseName}</p>
            </div>
            
            {enterpriseStatus.enterpriseExpiresAt && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {enterprisePassStatus === 'Active' ? 'Expires On' : 'Expired On'}
                </label>
                <p className="mt-1 text-sm text-gray-900 flex items-center">
                  <Calendar className="mr-1" size={16} />
                  {formatDate(enterpriseStatus.enterpriseExpiresAt)}
                </p>
              </div>
            )}
            
            {enterprisePassStatus === 'Expired' && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Enterprise Pass Expired</h3>
                    <p className="text-sm text-red-700 mt-1">
                      Your organization's enterprise pass has expired. Contact your administrator 
                      for renewal.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {enterprisePassStatus === 'Active' && (
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Your access is managed through your organization's enterprise account. 
                  Contact your administrator for any changes to your subscription.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default EnterprisePassStatus;